import React, { useMemo, useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Text } from './text';
import { Button } from './button';
import PendienteDialog from './PendienteDialog';
import PendienteEmailDialog from './PendienteEmailDialog';
import PendienteRecordarDialog from './PendienteRecordarDialog';
import { Dropdown, DropdownButton, DropdownMenu, DropdownItem, DropdownLabel } from './dropdown';
import PendienteNotas from './PendienteNotas';
import { useUser } from './UserContext';
import { useOrgPendientes } from '../hooks/usePendientes';
import { useOrgMiembros } from '../hooks/useOrgMiembros';
import { logBitacora } from '../utils/bitacora';
import {
  PENDIENTE_TIPO_LABELS,
  sePuedePedirAlFiado,
  clasificarPendiente,
  ordenarPorUrgencia,
  buildResolucion,
  isAbierto,
  normalizarAsignados,
  buildCancelacion,
} from '../utils/pendientes';
import {
  EnvelopeIcon, CheckCircleIcon, XMarkIcon, PencilSquareIcon,
  ClipboardDocumentListIcon, ChatBubbleLeftEllipsisIcon,
} from '@heroicons/react/24/outline';

const URGENCIA_STYLES = {
  vencido: 'bg-red-50 text-red-700 border-red-200',
  urgente: 'bg-amber-50 text-amber-700 border-amber-200',
  proximo: 'bg-cian-digital/10 text-azul-surelia border-cian-digital/30',
  normal: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  sinFecha: 'bg-zinc-50 text-zinc-500 border-zinc-200',
};

const urgenciaLabel = (row) => {
  if (row.urgencia === 'sinFecha') return 'Sin fecha';
  const d = row.diasRestantes;
  if (d < 0) return `Venció hace ${Math.abs(d)} d`;
  if (d === 0) return 'Vence hoy';
  return `En ${d} d`;
};

/**
 * Pendientes in context — the panel every surface embeds.
 *
 * `scope` says how narrow the view is; the panel filters the org's pendientes
 * down to it and passes the same information back to the dialog as `preset`, so
 * creating from here never asks for what the surface already knows. From a
 * fianza that means a compromiso is a title, a date and Guardar.
 *
 * Reads the org-wide listener rather than its own query: the Pendientes page,
 * Pulso and every panel share one subscription.
 *
 * @param {Object} props
 * @param {Object} props.scope  - {clienteId?, convenioId?, contratoId?, fianzaId?}
 * @param {Object} [props.preset]  - extra context for the create dialog
 *   (contratoTitulo, numeroFianza, afianzadoraRaw, tipo)
 * @param {string} [props.clientName]
 * @param {string} [props.titulo]
 * @param {boolean} [props.compacto]  - hide the header, for tab bodies
 * @param {'caja'|'linea'} [props.vacio]  - how the empty state renders
 * @param {boolean} [props.tarjeta]  - draw the card wrapper (dropped when empty)
 * @param {boolean} [props.plano]  - rows as a plain divided list, no box around them (OtView)
 * @param {Function} [props.Icon]  - heroicon for the heading
 * @param {Array<string>} [props.tipos]  - creatable tipos here (null = all)
 * @param {boolean} [props.correoFiado]  - offer "Solicitar al fiado" next to
 *   "Recordar al responsable" (fiado-context surfaces only)
 */
export function PendientesPanel({
  scope = {},
  preset = null,
  clientName = '',
  titulo = 'Pendientes',
  compacto = false,
  nuevoLabel = 'Nuevo',
  // 'caja' draws a dashed empty box; 'linea' renders nothing but the add
  // button, for surfaces where an empty panel would be dead vertical space.
  vacio = 'caja',
  // Draw the surrounding card here instead of in the parent, so an empty panel
  // can drop the chrome: a white card holding only a heading reads as broken.
  tarjeta = false,
  plano = false,
  Icon = null,
  // Which tipos this surface creates (null = all). The tipo dictates the
  // level, so a fianza offers the compromiso and a contrato offers actas and
  // convenios modificatorios; nothing else makes sense from there.
  tipos = null,
  // Mailing the fiado is only offered where the agente is looking at the
  // fiado (ficha, contrato, fianza). Most pendientes are internal, and from
  // an OT or a cross-cartera list a one-click mail to the client is a
  // liability; there the envelope only reminds the team.
  correoFiado = false,
}) {
  const { user, userData, isClient } = useUser();
  const raw = useOrgPendientes();
  const { nombreDe } = useOrgMiembros();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [emailTarget, setEmailTarget] = useState(null);
  const [recordarTarget, setRecordarTarget] = useState(null);
  const [verCerrados, setVerCerrados] = useState(false);
  const [notasAbiertas, setNotasAbiertas] = useState(null);
  const [busy, setBusy] = useState(false);

  // Narrowest scope wins: a fianza-scoped panel shows only that fianza's, not
  // everything under its cliente. A contrato-scoped one includes its fianzas'
  // because the contrato is the thing the agente is looking at.
  const filas = useMemo(() => {
    if (!raw) return null;
    const now = Date.now();
    const match = (p) => {
      // An OT's panel shows what was filed FROM that OT, whatever its level.
      if (scope.otId) return p.otId === scope.otId;
      if (scope.fianzaId) return p.fianzaId === scope.fianzaId;
      if (scope.contratoId) return p.contratoId === scope.contratoId;
      if (scope.convenioId) return p.convenioId === scope.convenioId;
      if (scope.clienteId) return p.clienteId === scope.clienteId;
      return false;
    };
    const rows = raw.filter(match).map((p) => {
      const u = clasificarPendiente(p, now);
      return { ...p, origen: 'pendiente', urgencia: u.code, diasRestantes: u.dias };
    });
    return ordenarPorUrgencia(
      rows.filter((p) => (verCerrados ? true : isAbierto(p)))
    );
  }, [raw, scope.otId, scope.fianzaId, scope.contratoId, scope.convenioId, scope.clienteId, verCerrados]);

  const cerrados = useMemo(
    () => (raw || []).filter((p) => {
      const inScope = scope.otId ? p.otId === scope.otId
        : scope.fianzaId ? p.fianzaId === scope.fianzaId
          : scope.contratoId ? p.contratoId === scope.contratoId
            : scope.convenioId ? p.convenioId === scope.convenioId
              : scope.clienteId ? p.clienteId === scope.clienteId : false;
      return inScope && !isAbierto(p);
    }).length,
    [raw, scope.otId, scope.fianzaId, scope.contratoId, scope.convenioId, scope.clienteId]
  );

  const bitacoraFor = (row, description) => ({
    action: 'UPDATE',
    collection: row.convenioId ? 'convenios' : 'clientes',
    documentId: row.convenioId || row.clienteId,
    user,
    userData,
    metadata: {
      description,
      clientId: row.clienteId || null,
      convenioId: row.convenioId || null,
      contratoId: row.contratoId || null,
    },
  });

  const resolver = async (row) => {
    setBusy(true);
    try {
      await updateDoc(doc(db, 'pendientes', row.id),
        buildResolucion({ resueltoCon: { tipo: 'manual' }, user, nombre: userData?.nombre || userData?.correo || null }));
      await logBitacora(bitacoraFor(row, `Resolvió pendiente: ${row.titulo}`));
    } catch (e) {
      console.error('resolver pendiente failed', e);
    } finally {
      setBusy(false);
    }
  };

  const cancelar = async (row) => {
    setBusy(true);
    try {
      await updateDoc(doc(db, 'pendientes', row.id),
        buildCancelacion({ user, nombre: userData?.nombre || userData?.correo || null }));
      await logBitacora(bitacoraFor(row, `Canceló pendiente: ${row.titulo}`));
    } catch (e) {
      console.error('cancelar pendiente failed', e);
    } finally {
      setBusy(false);
    }
  };

  if (isClient) return null;

  const abiertos = (filas || []).filter(isAbierto).length;
  const sinContenido = !filas || filas.length === 0;
  // Card chrome only when there is something inside it.
  const wrapper = tarjeta && !sinContenido
    ? 'space-y-3 bg-white rounded-lg border border-gray-200 shadow-sm p-4'
    : 'space-y-3';

  return (
    <div className={wrapper}>
      {!compacto && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            {Icon && <Icon className="w-4 h-4 text-azul-surelia" />}
            <Text className="text-sm font-semibold text-azul-surelia">{titulo}</Text>
            {abiertos > 0 && (
              <span className="rounded-full bg-azul-surelia/10 text-azul-surelia text-[11px] font-bold px-1.5">
                {abiertos}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {cerrados > 0 && (
              <button
                onClick={() => setVerCerrados((v) => !v)}
                className="text-xs text-azul-surelia hover:underline"
              >
                {verCerrados ? 'Solo abiertos' : `Ver cerrados (${cerrados})`}
              </button>
            )}
            <Button plain onClick={() => { setEditing(null); setDialogOpen(true); }}>
              {nuevoLabel}
            </Button>
          </div>
        </div>
      )}

      {filas === null ? (
        vacio === 'linea' ? null : (
          <div className="flex justify-center py-6">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-azul-surelia"></div>
          </div>
        )
      ) : filas.length === 0 ? (
        vacio === 'linea' ? null : (
          <div className="flex flex-col items-center gap-2 py-8 text-zinc-400 rounded-lg border border-dashed border-zinc-200">
            <ClipboardDocumentListIcon className="w-8 h-8" />
            <Text className="text-xs text-zinc-500">Sin pendientes.</Text>
            {compacto && (
              <Button plain onClick={() => { setEditing(null); setDialogOpen(true); }}>
                {nuevoLabel}
              </Button>
            )}
          </div>
        )
      ) : (
        <div className={plano ? 'divide-y divide-zinc-100' : 'rounded-lg border border-zinc-200 divide-y divide-zinc-100'}>
          {filas.map((row) => (
            <div
              key={row.id}
              className={plano ? 'flex items-start gap-3 py-2' : 'flex items-start gap-3 px-3 py-2.5'}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <Text className={`text-sm font-medium ${
                    isAbierto(row) ? 'text-zinc-800' : 'text-zinc-400 line-through'
                  }`}>
                    {row.titulo}
                  </Text>
                </div>
                <Text className="text-xs text-zinc-500">
                  {PENDIENTE_TIPO_LABELS[row.tipo] || row.tipo}
                  {row.afianzadora ? ` · ${row.afianzadora}` : ''}
                  {(() => {
                    const otros = normalizarAsignados(row).filter((u) => u !== user?.uid).map(nombreDe).filter(Boolean);
                    return otros.length ? ` · ${otros.join(', ')}` : '';
                  })()}
                </Text>
                {row.detalle && (
                  <Text className="text-xs text-zinc-600 mt-1">{row.detalle}</Text>
                )}
                {notasAbiertas === row.id && (
                  <div className="mt-2">
                    <PendienteNotas pendiente={row} compacto />
                  </div>
                )}
              </div>
              <div className="flex-shrink-0 flex items-center gap-1">
                <button
                  title="Actualizaciones"
                  onClick={() => setNotasAbiertas((v) => (v === row.id ? null : row.id))}
                  className={`p-1.5 rounded hover:bg-zinc-100 inline-flex items-center gap-0.5 text-[11px] ${
                    notasAbiertas === row.id ? 'text-azul-surelia' : 'text-zinc-400'
                  }`}
                >
                  <ChatBubbleLeftEllipsisIcon className="w-4 h-4" />
                  {(row.notasInternas || []).length > 0 ? row.notasInternas.length : ''}
                </button>
                {isAbierto(row) && (
                  <span className={`hidden sm:inline-block rounded-full border px-2 py-0.5 text-xs ${
                    URGENCIA_STYLES[row.urgencia] || URGENCIA_STYLES.normal
                  }`}>
                    {urgenciaLabel(row)}
                  </span>
                )}
                {isAbierto(row) && (
                  <>
                    {correoFiado && sePuedePedirAlFiado(row.tipo) && row.clienteId ? (
                      <Dropdown>
                        <DropdownButton
                          as="button"
                          title="Correo"
                          className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500"
                        >
                          <EnvelopeIcon className="w-4 h-4" />
                        </DropdownButton>
                        <DropdownMenu anchor="bottom end">
                          <DropdownItem onClick={() => setRecordarTarget(row)}>
                            <DropdownLabel>Recordar al responsable</DropdownLabel>
                          </DropdownItem>
                          <DropdownItem onClick={() => setEmailTarget({
                            clientId: row.clienteId,
                            clientName: clientName || '',
                            pendientes: [row],
                          })}>
                            <DropdownLabel>Solicitar al fiado</DropdownLabel>
                          </DropdownItem>
                        </DropdownMenu>
                      </Dropdown>
                    ) : (
                      <button
                        title="Recordar al responsable"
                        onClick={() => setRecordarTarget(row)}
                        className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500"
                      >
                        <EnvelopeIcon className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      title="Editar"
                      onClick={() => { setEditing(row); setDialogOpen(true); }}
                      className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500"
                    >
                      <PencilSquareIcon className="w-4 h-4" />
                    </button>
                    <button
                      title="Marcar resuelto"
                      disabled={busy}
                      onClick={() => resolver(row)}
                      className="p-1.5 rounded hover:bg-green-50 text-green-600"
                    >
                      <CheckCircleIcon className="w-4 h-4" />
                    </button>
                    <button
                      title="Cancelar"
                      disabled={busy}
                      onClick={() => cancelar(row)}
                      className="p-1.5 rounded hover:bg-zinc-100 text-zinc-400"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <PendienteDialog
        isOpen={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        pendiente={editing}
        preset={editing ? null : { ...scope, ...(preset || {}) }}
        tiposPermitidos={tipos}
      />

      {emailTarget && (
        <PendienteEmailDialog
          open
          onClose={() => setEmailTarget(null)}
          clientId={emailTarget.clientId}
          clientName={emailTarget.clientName}
          pendientes={emailTarget.pendientes}
        />
      )}

      {recordarTarget && (
        <PendienteRecordarDialog
          open
          onClose={() => setRecordarTarget(null)}
          pendiente={recordarTarget}
        />
      )}
    </div>
  );
}

export default PendientesPanel;
